import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { CheckCircle2, AlertCircle, ChevronRight, Database, FileSpreadsheet, Loader2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useEffect, useState } from 'react';

// CR-5320 + CR-5321: Drill-down modal for the Data Import page. Shared by
// both the top summary cards (Types / Records / Errors) and the
// per-row Created / Updated / Failed cells in the Import History table.
// All data is already on the client — this is a presentational wrapper
// over the StepResult[] the page already maintains; no extra fetch.

/**
 * One upload attempt, as the errors view reads it.
 *
 * ImportStep is reset on every re-upload, so it only holds the latest attempt;
 * a run records each one. `errors` is the first 20 reasons — when
 * `error_count` exceeds that, the rest are fetched on expand.
 */
export interface ImportRunLite {
  /** Null for attempts synthesised from steps recorded before run history
   *  existed — there is no run record to fetch, so they cannot expand. */
  id: number | null;
  step_number: number;
  status: string;
  file_name: string;
  rows_failed: number;
  errors: string[];
  error_count: number;
  started_by_name: string | null;
  completed_at: string | null;
}

export interface ImportStepLite {
  id: number;
  step_number: number;
  status: string;
  site_name: string | null;
  file_name: string;
  rows_total: number;
  rows_created: number;
  rows_updated: number;
  rows_skipped: number;
  rows_failed: number;
  errors: string[];
  completed_at: string | null;
  started_at: string | null;
}

export type DetailMode =
  | 'types'      // CR-5321: clicked "Data Types Imported"
  | 'created'    // CR-5321: clicked "Records Created"
  | 'errors'     // CR-5321: clicked "Errors"
  | 'step';      // CR-5320: clicked a per-row cell — single step, focus on metric

interface ImportTypeMeta {
  step: number;
  shortName: string;
  name: string;
}

interface ImportDetailModalProps {
  open: boolean;
  onClose: () => void;
  mode: DetailMode;
  steps: ImportStepLite[];
  /** Errors mode only — every upload attempt, newest first. */
  runs?: ImportRunLite[];
  /** Loads the full reason list for one attempt when it is expanded. */
  fetchRunErrors?: (runId: number) => Promise<string[]>;
  importTypes: ImportTypeMeta[];
  // For mode='step', which metric was clicked. Drives which counts /
  // rows are emphasised in the rendered detail.
  stepFocus?: 'created' | 'updated' | 'failed';
  title: string;
  description?: string;
}

export default function ImportDetailModal({
  open,
  onClose,
  mode,
  steps,
  runs = [],
  fetchRunErrors,
  importTypes,
  stepFocus,
  title,
  description,
}: ImportDetailModalProps) {
  // Full reason lists pulled on demand, keyed by run id. Reset when the modal
  // closes so a re-open reflects whatever the page reloaded.
  const [expanded, setExpanded] = useState<Record<number, string[]>>({});
  const [loadingRun, setLoadingRun] = useState<number | null>(null);
  const [expandError, setExpandError] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!open) {
      setExpanded({});
      setLoadingRun(null);
      setExpandError({});
    }
  }, [open]);

  const loadAllErrors = async (runId: number) => {
    if (!fetchRunErrors || expanded[runId] || loadingRun === runId) return;
    setLoadingRun(runId);
    setExpandError(prev => { const next = { ...prev }; delete next[runId]; return next; });
    try {
      const all = await fetchRunErrors(runId);
      setExpanded(prev => ({ ...prev, [runId]: all }));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Could not load the full list';
      setExpandError(prev => ({ ...prev, [runId]: message }));
    } finally {
      setLoadingRun(null);
    }
  };

  const nameForStep = (stepNumber: number) => {
    const t = importTypes.find(x => x.step === stepNumber);
    return t ? t.shortName : `Step ${stepNumber}`;
  };

  // Aggregate totals — used in headers
  const totals = steps.reduce(
    (acc, s) => ({
      created: acc.created + (s.rows_created || 0),
      updated: acc.updated + (s.rows_updated || 0),
      failed: acc.failed + (s.rows_failed || 0),
      types: acc.types + 1,
    }),
    { created: 0, updated: 0, failed: 0, types: 0 },
  );

  const renderTypesMode = () => (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {totals.types} data {totals.types === 1 ? 'type' : 'types'} imported. Each row below is the latest completed import of that type.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Data type</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>File</TableHead>
            <TableHead className="text-right">Created</TableHead>
            <TableHead className="text-right">Updated</TableHead>
            <TableHead className="text-right">Failed</TableHead>
            <TableHead>When</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {steps.map(s => (
            <TableRow key={s.step_number}>
              <TableCell className="font-medium">{nameForStep(s.step_number)}</TableCell>
              <TableCell>
                <Badge className={s.rows_failed > 0 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>
                  {s.rows_failed > 0 ? 'partial' : s.status}
                </Badge>
              </TableCell>
              <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{s.file_name || '—'}</TableCell>
              <TableCell className="text-right text-green-700 font-medium">{s.rows_created || 0}</TableCell>
              <TableCell className="text-right text-blue-700">{s.rows_updated || 0}</TableCell>
              <TableCell className="text-right text-red-700">{s.rows_failed || 0}</TableCell>
              <TableCell className="text-xs text-muted-foreground">
                {s.completed_at ? formatDistanceToNow(new Date(s.completed_at), { addSuffix: true }) : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  const renderCreatedMode = () => {
    const withCreated = steps.filter(s => (s.rows_created || 0) > 0)
      .sort((a, b) => (b.rows_created || 0) - (a.rows_created || 0));
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {totals.created.toLocaleString()} records created across {withCreated.length} {withCreated.length === 1 ? 'import' : 'imports'}.
          </p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data type</TableHead>
              <TableHead>File</TableHead>
              <TableHead className="text-right">Created</TableHead>
              <TableHead>When</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {withCreated.map(s => (
              <TableRow key={s.step_number}>
                <TableCell className="font-medium">{nameForStep(s.step_number)}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[240px] truncate">{s.file_name || '—'}</TableCell>
                <TableCell className="text-right text-green-700 font-semibold">{(s.rows_created || 0).toLocaleString()}</TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {s.completed_at ? formatDistanceToNow(new Date(s.completed_at), { addSuffix: true }) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderErrorBlock = (run: ImportRunLite, muted = false) => {
    const shown = (run.id !== null && expanded[run.id]) || run.errors || [];
    const hasMore = run.error_count > shown.length;
    const isLoading = run.id !== null && loadingRun === run.id;
    const failedToLoad = run.id !== null ? expandError[run.id] : undefined;
    // Steps predating run history have no record to fetch, so say the list is
    // partial rather than offering a button that cannot work.
    const canExpand = run.id !== null && !!fetchRunErrors;

    return (
      <div key={run.id ?? `step-${run.step_number}-${run.completed_at}`}
           className={`border rounded-lg p-3 ${muted ? 'bg-muted/30' : ''}`}>
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <AlertCircle className={`h-4 w-4 shrink-0 ${muted ? 'text-muted-foreground' : 'text-red-600'}`} />
            <span className="font-medium text-sm truncate">{nameForStep(run.step_number)}</span>
            <Badge className={`text-[10px] shrink-0 ${muted ? 'bg-muted text-muted-foreground' : 'bg-red-100 text-red-800'}`}>
              {(run.rows_failed || 0).toLocaleString()} failed
            </Badge>
          </div>
          <div className="text-xs text-muted-foreground text-right shrink-0">
            <div className="truncate max-w-[180px]">{run.file_name || '—'}</div>
            <div>
              {run.completed_at ? formatDistanceToNow(new Date(run.completed_at), { addSuffix: true }) : '—'}
              {run.started_by_name ? ` · ${run.started_by_name}` : ''}
            </div>
          </div>
        </div>

        {shown.length > 0 ? (
          <div className={`rounded p-2 max-h-64 overflow-y-auto border ${muted ? 'bg-background' : 'bg-red-50 border-red-100'}`}>
            <ul className={`text-xs space-y-1 list-disc pl-5 ${muted ? 'text-muted-foreground' : 'text-red-800'}`}>
              {shown.map((err, i) => <li key={i}>{err}</li>)}
            </ul>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">No detailed error messages captured for this import.</p>
        )}

        {hasMore && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              Showing {shown.length.toLocaleString()} of {run.error_count.toLocaleString()}.
            </span>
            {canExpand ? (
              <Button
                type="button" variant="outline" size="sm" className="h-7 text-xs"
                disabled={isLoading}
                onClick={() => run.id !== null && loadAllErrors(run.id)}
              >
                {isLoading && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                {isLoading ? 'Loading…' : `Show all ${run.error_count.toLocaleString()}`}
              </Button>
            ) : (
              <span className="text-xs text-muted-foreground italic">
                The rest were not kept for this import.
              </span>
            )}
          </div>
        )}
        {failedToLoad && (
          <p className="text-xs text-red-600 mt-1">{failedToLoad}</p>
        )}
      </div>
    );
  };

  const renderErrorsMode = () => {
    const withErrors = runs.filter(r => (r.rows_failed || 0) > 0 || r.error_count > 0);

    // `runs` arrives newest first, so the first attempt seen for a step_number
    // is the current one and everything after it is history. Splitting here
    // rather than upstream keeps the card's "Errors" number meaning the latest
    // run, while still making earlier attempts readable — before this,
    // re-uploading a corrected file reset the step and put the previous
    // attempt's reasons permanently out of reach.
    const seen = new Set<number>();
    const current: ImportRunLite[] = [];
    const previous: ImportRunLite[] = [];
    for (const run of withErrors) {
      if (seen.has(run.step_number)) previous.push(run);
      else { seen.add(run.step_number); current.push(run); }
    }
    const currentFailed = current.reduce((sum, r) => sum + (r.rows_failed || 0), 0);

    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {currentFailed.toLocaleString()} {currentFailed === 1 ? 'row' : 'rows'} failed in the most recent import of {current.length} {current.length === 1 ? 'data type' : 'data types'}.
          {current.length === 0 && ' No errors yet — green across the board.'}
        </p>
        {current.map(run => renderErrorBlock(run))}

        {previous.length > 0 && (
          <div className="space-y-3 pt-2">
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                Previous runs ({previous.length})
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <p className="text-xs text-muted-foreground">
              Earlier uploads of the same data types. Kept for reference — these rows may already have been fixed and re-uploaded.
            </p>
            {previous.map(run => renderErrorBlock(run, true))}
          </div>
        )}
      </div>
    );
  };

  const renderStepMode = () => {
    const s = steps[0];
    if (!s) return <p className="text-sm text-muted-foreground">No data.</p>;
    const showErrors = stepFocus === 'failed' && s.errors && s.errors.length > 0;
    return (
      <div className="space-y-4">
        <div className="rounded-lg border p-4 bg-muted/30">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-sm">{nameForStep(s.step_number)}</span>
            </div>
            <Badge className={
              s.status === 'completed' && s.rows_failed > 0 ? 'bg-amber-100 text-amber-800' :
              s.status === 'completed' ? 'bg-green-100 text-green-800' :
              s.status === 'failed' ? 'bg-red-100 text-red-800' :
              'bg-blue-100 text-blue-800'
            }>{s.status === 'completed' && s.rows_failed > 0 ? 'partial' : s.status}</Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <Stat label="Total rows" value={(s.rows_total || 0).toLocaleString()} />
            <Stat label="Created" value={(s.rows_created || 0).toLocaleString()} highlight={stepFocus === 'created' ? 'green' : undefined} />
            <Stat label="Updated" value={(s.rows_updated || 0).toLocaleString()} highlight={stepFocus === 'updated' ? 'blue' : undefined} />
            <Stat label="Failed" value={(s.rows_failed || 0).toLocaleString()} highlight={stepFocus === 'failed' ? 'red' : undefined} />
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-muted-foreground">
            <div><span className="font-medium">File:</span> {s.file_name || '—'}</div>
            <div><span className="font-medium">Company:</span> {s.site_name || '—'}</div>
            <div><span className="font-medium">When:</span> {s.completed_at ? formatDistanceToNow(new Date(s.completed_at), { addSuffix: true }) : '—'}</div>
            <div><span className="font-medium">Skipped:</span> {(s.rows_skipped || 0).toLocaleString()}</div>
          </div>
        </div>

        {showErrors && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <span className="font-medium text-sm text-red-800">Errors ({s.errors.length})</span>
            </div>
            <div className="max-h-64 overflow-y-auto">
              <ul className="text-xs text-red-800 space-y-1 list-disc pl-5">
                {s.errors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </div>
          </div>
        )}

        {stepFocus === 'created' && (s.rows_created || 0) > 0 && (
          <p className="text-xs text-muted-foreground border-l-2 border-green-400 pl-3 py-1">
            <CheckCircle2 className="h-3 w-3 inline mr-1 text-green-600" />
            {s.rows_created.toLocaleString()} new {nameForStep(s.step_number).toLowerCase()} {s.rows_created === 1 ? 'record was' : 'records were'} added by this import. Visit the relevant area of the platform to review them.
          </p>
        )}
        {stepFocus === 'updated' && (s.rows_updated || 0) > 0 && (
          <p className="text-xs text-muted-foreground border-l-2 border-blue-400 pl-3 py-1">
            <ChevronRight className="h-3 w-3 inline mr-1 text-blue-600" />
            {s.rows_updated.toLocaleString()} existing {nameForStep(s.step_number).toLowerCase()} {s.rows_updated === 1 ? 'record was' : 'records were'} updated.
          </p>
        )}
        {stepFocus === 'failed' && !showErrors && (s.rows_failed || 0) > 0 && (
          <p className="text-xs text-muted-foreground italic">No detailed error messages captured for this import.</p>
        )}
      </div>
    );
  };

  let body: React.ReactNode;
  if (mode === 'types') body = renderTypesMode();
  else if (mode === 'created') body = renderCreatedMode();
  else if (mode === 'errors') body = renderErrorsMode();
  else body = renderStepMode();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            {title}
          </DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: 'green' | 'blue' | 'red' }) {
  const cls =
    highlight === 'green' ? 'text-green-700 font-semibold' :
    highlight === 'blue' ? 'text-blue-700 font-semibold' :
    highlight === 'red' ? 'text-red-700 font-semibold' :
    'text-foreground';
  return (
    <div className="bg-background rounded p-2 border">
      <p className="text-[10px] uppercase text-muted-foreground tracking-wide">{label}</p>
      <p className={`text-base ${cls}`}>{value}</p>
    </div>
  );
}
